import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useTranslation } from "@/i18n/use-translation";
import { formatNumber } from "@/lib/format";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PaginationBarProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
  /** Pin to the bottom of the viewport (opt-in so other pages are unaffected). */
  sticky?: boolean;
}

function pageNumbers(current: number, total: number): (number | "...")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | "...")[] = [1];
  const left = Math.max(2, current);
  const right = Math.min(total - 1, current + 2);

  if (left > 2) pages.push("...");
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < total - 1) pages.push("...");
  if (total > 1) pages.push(total);

  return pages;
}

export function PaginationBar({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [20, 50, 100],
  sticky = false,
}: PaginationBarProps) {
  const { locale, t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const displayPage = page + 1; // 0-indexed to 1-indexed
  const start = total === 0 ? 0 : page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, total);

  if (total === 0) return null;

  return (
    <div className={`-mx-6 -mb-6 shrink-0${sticky ? " sticky bottom-0 z-10 bg-background" : ""}`}>
      <div className="mx-2 h-px bg-gray-200" />
      <div className="flex h-11 items-center justify-between px-6">
        <p className="text-xs text-gray-500">
          {t("common.showing", {
            start: formatNumber(locale, start),
            end: formatNumber(locale, end),
            total: formatNumber(locale, total),
          })}
        </p>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">{t("common.rowsPerPage")}</span>
            <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
              <SelectTrigger className="h-8 w-[70px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="size-8"
              disabled={page === 0}
              onClick={() => onPageChange(page - 1)}
              aria-label={t("common.previousPage")}
            >
              <ChevronLeft className="size-4" />
            </Button>

            {/* Page numbers — hidden on mobile */}
            <div className="hidden items-center gap-0.5 sm:flex">
              {pageNumbers(displayPage, totalPages).map((p, i) =>
                p === "..." ? (
                  <span
                    key={`ellipsis-${i}`}
                    className="flex size-8 items-center justify-center text-xs text-gray-400"
                  >
                    ...
                  </span>
                ) : (
                  <Button
                    key={p}
                    variant={p === displayPage ? "default" : "ghost"}
                    size="icon"
                    className={`size-8 text-xs ${p === displayPage ? "bg-gray-900 text-white hover:bg-gray-800" : "text-gray-600"}`}
                    onClick={() => onPageChange(p - 1)}
                  >
                    {p}
                  </Button>
                ),
              )}
            </div>

            {/* Mobile: just show page X of Y */}
            <span className="px-2 text-xs text-gray-500 sm:hidden">
              {displayPage} / {totalPages}
            </span>

            <Button
              variant="outline"
              size="icon"
              className="size-8"
              disabled={displayPage >= totalPages}
              onClick={() => onPageChange(page + 1)}
              aria-label={t("common.nextPage")}
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
